'use client';

import { createRoot, type Root } from 'react-dom/client';

import {
  CourseCardWidget,
  mapCourseCardToCartItem,
  type CourseCardItem,
} from '@/features/course-card';
import { createWidgetMount } from '@/shared/utils/widget-mount';

import {
  OBSERVED_COURSE_CARD_WIDGET_ATTRIBUTES,
  isCourseCardItem,
  readCourseCardWidgetProps,
} from './course-card-element-props';
import type { CourseCardEventName } from './types';

function dispatchCourseCardEvent(
  element: HTMLElement,
  name: CourseCardEventName,
  detail?: unknown,
  cancelable = false,
): boolean {
  return element.dispatchEvent(
    new CustomEvent(name, {
      bubbles: true,
      cancelable,
      composed: true,
      detail,
    }),
  );
}

export class CourseCardWidgetElement extends HTMLElement {
  static observedAttributes = OBSERVED_COURSE_CARD_WIDGET_ATTRIBUTES;

  private root: Root | null = null;
  private mountNode: HTMLDivElement | null = null;
  private renderTimer: number | null = null;
  private unmountTimer: number | null = null;
  private courseValue: CourseCardItem | null = null;
  private pendingReady = false;

  get course(): CourseCardItem | null {
    return this.courseValue;
  }

  set course(value: CourseCardItem | null) {
    this.courseValue = isCourseCardItem(value) ? value : null;
    this.scheduleRender();
  }

  connectedCallback() {
    if (this.unmountTimer !== null) {
      window.clearTimeout(this.unmountTimer);
      this.unmountTimer = null;
    }

    if (!this.mountNode) {
      this.mountNode = createWidgetMount(this, {
        rootAttribute: 'data-adipa-course-card-widget-root',
      }) as HTMLDivElement;
    }

    if (!this.root) {
      this.root = createRoot(this.mountNode);
    }

    this.scheduleRender();
  }

  disconnectedCallback() {
    if (!this.root || this.unmountTimer !== null) return;

    if (this.renderTimer !== null) {
      window.clearTimeout(this.renderTimer);
      this.renderTimer = null;
    }

    const root = this.root;
    this.unmountTimer = window.setTimeout(() => {
      this.unmountTimer = null;
      if (this.isConnected || this.root !== root) return;
      root.unmount();
      this.root = null;
    }, 0);
  }

  attributeChangedCallback(name: string) {
    // A favorite toggle only flips the heart icon; it never changes the card's
    // height. Re-dispatching `adipa-course-card-ready` for it would re-run the
    // grid/swiper equalize (isotope layout) and cause a visible height jump, so
    // we still re-render the heart but skip the ready event for is-favorite.
    this.scheduleRender(name !== 'is-favorite');
  }

  private scheduleRender(dispatchReady = true) {
    if (dispatchReady) this.pendingReady = true;
    if (!this.root || this.renderTimer !== null) return;

    this.renderTimer = window.setTimeout(() => {
      this.renderTimer = null;
      this.renderWidget();
    }, 0);
  }

  private renderWidget() {
    if (!this.root) return;

    const props = readCourseCardWidgetProps(this, this.courseValue);

    const shouldDispatchReady = this.pendingReady;
    this.pendingReady = false;
    if (shouldDispatchReady) {
      dispatchCourseCardEvent(this, 'adipa-course-card-ready', {
        course: props.course,
        variant: props.variant,
        isFavorite: props.isFavorite,
        isInCart: props.isInCart,
        isSelectable: props.isSelectable,
        isSelected: props.isSelected,
        cartIntent: props.cartIntent,
        selectedCourseId: props.selectedCourseId,
      });
    }

    if (!props.course) {
      this.root.render(
        <div className="rounded-adipa-lg border border-adipa-border bg-adipa-surface p-4 text-sm text-adipa-text-secondary">
          Curso no disponible.
        </div>,
      );
      return;
    }

    this.root.render(
      <CourseCardWidget
        course={props.course}
        variant={props.variant}
        isFavorite={props.isFavorite}
        isInCart={props.isInCart}
        isLoggedIn={props.isLoggedIn}
        isCyberActive={props.isCyberActive}
        isSelectable={props.isSelectable}
        isSelected={props.isSelected}
        selectionName={props.selectionName}
        selectionValue={props.selectionValue}
        hideActions={props.hideActions}
        hidePrice={props.hidePrice}
        prioritizeImage={props.prioritizeImage}
        actionLabel={props.actionLabel}
        checkoutLabel={props.checkoutLabel}
        cartIntent={props.cartIntent}
        selectedCourseId={props.selectedCourseId}
        onAddToCart={(course, cartItem, context) =>
          dispatchCourseCardEvent(this, 'adipa-course-card-add-to-cart', {
            course,
            cartItem,
            intent: context.intent,
            selectedCourseId: context.selectedCourseId,
          })
        }
        onAddToCartAnimationComplete={(course, cartItem, context) =>
          dispatchCourseCardEvent(this, 'adipa-course-card-add-animation-complete', {
            course,
            cartItem,
            intent: context.intent,
            selectedCourseId: context.selectedCourseId,
          })
        }
        onCheckout={(course, cartItem, context) =>
          dispatchCourseCardEvent(this, 'adipa-course-card-checkout', {
            course,
            cartItem,
            intent: context.intent,
            selectedCourseId: context.selectedCourseId,
          })
        }
        onFavoriteToggle={(course, nextFavorite) =>
          dispatchCourseCardEvent(this, 'adipa-course-card-favorite-toggle', {
            course,
            nextFavorite,
          })
        }
        onSelectionChange={(course, nextSelected, value) => {
          if (nextSelected) {
            this.setAttribute('selected', '');
          } else {
            this.removeAttribute('selected');
          }

          dispatchCourseCardEvent(this, 'adipa-course-card-selection-change', {
            course,
            selected: nextSelected,
            value,
          });
        }}
        onNavigate={(course, href) => {
          const shouldNavigate = dispatchCourseCardEvent(
            this,
            'adipa-course-card-navigate',
            {
              course,
              href,
              cartItem: mapCourseCardToCartItem(course),
            },
            true,
          );

          if (shouldNavigate && typeof window !== 'undefined') {
            window.location.assign(href);
          }
        }}
      />,
    );
  }
}
